/**
 * Login hook
 * Handles login logic and super admin creation
 */

import { useState } from 'react';
import type { UserProfile } from '../../../shared/types';
import { signIn, signOut } from '../services/auth.service';
import { getUserProfile } from '../services/user.service';
import { getErrorMessage } from '@shared/utils/error';

export const useLogin = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const login = async (email: string, password: string): Promise<UserProfile | null> => {
    setLoading(true);
    setError('');

    try {
      const userCredential = await signIn(email, password);
      const userProfile = await getUserProfile(userCredential.user.uid);

      if (!userProfile) {
        throw new Error('User profile not found');
      }

      // Allow super_admin, company, and manager users
      if (!userProfile.userType || !['super_admin', 'company', 'manager'].includes(userProfile.userType)) {
        await signOut();
        throw new Error('Access denied. Staff users should use the mobile app.');
      }

      return userProfile;
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    login,
    loading,
    error,
    setError,
  };
};
